import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { Reveal, Eyebrow } from "./shared";
import { resolveUrl } from "@/lib/api";

export const Gallery = ({ albums = [] }) => {
  const [active, setActive] = useState(null); // album index
  const [photoIdx, setPhotoIdx] = useState(0);

  const album = active !== null ? albums[active] : null;
  const photos = album?.photos || [];

  const open = (i) => {
    setActive(i);
    setPhotoIdx(0);
  };
  const close = useCallback(() => setActive(null), []);
  const next = useCallback(() => setPhotoIdx((p) => (p + 1) % photos.length), [photos.length]);
  const prev = useCallback(() => setPhotoIdx((p) => (p - 1 + photos.length) % photos.length), [photos.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, next, prev]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 md:py-32 bg-navy-soft border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <Eyebrow testid="gallery-eyebrow">Dokumentasi</Eyebrow>
              <h2 className="mt-6 font-heading font-bold text-3xl md:text-5xl tracking-tight">Momen di balik prestasi</h2>
            </div>
            <p className="text-slate-400 max-w-sm text-sm">Klik setiap album untuk melihat seluruh dokumentasi kegiatan, event, dan penganugerahan juara.</p>
          </div>
        </Reveal>

        <div className="mt-12 columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {albums.map((g, i) => (
            <motion.button
              type="button"
              key={g.id || i}
              onClick={() => open(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              data-testid={`gallery-album-${i}`}
              className={`mb-5 w-full break-inside-avoid group relative overflow-hidden rounded-2xl border border-white/10 block ${i % 3 === 0 ? "h-[380px]" : "h-[260px]"}`}
            >
              <img src={resolveUrl(g.cover_url)} alt={g.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
              <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full glass px-3 py-1.5 text-xs font-semibold">
                <Images className="w-3.5 h-3.5 text-gold" /> {(g.photos || []).length}
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-left">
                <h3 className="font-heading font-semibold text-lg text-white">{g.title}</h3>
                {g.subtitle && <p className="text-sm text-slate-300 mt-0.5">{g.subtitle}</p>}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {album && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-navy/95 backdrop-blur-md grid place-items-center p-4 md:p-10"
            onClick={close}
          >
            <button onClick={close} aria-label="Tutup" data-testid="lightbox-close" className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full border border-white/15 text-white hover:bg-white/10 transition">
              <X />
            </button>

            <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-4 text-center">
                <h3 className="font-heading font-bold text-2xl">{album.title}</h3>
                <p className="text-sm text-slate-400">
                  {photoIdx + 1} / {photos.length}
                </p>
              </div>
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black/40">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={photoIdx}
                    src={resolveUrl(photos[photoIdx])}
                    alt={`${album.title} ${photoIdx + 1}`}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="w-full max-h-[62vh] object-contain bg-black"
                  />
                </AnimatePresence>
                {photos.length > 1 && (
                  <>
                    <button onClick={prev} aria-label="Sebelumnya" data-testid="lightbox-prev" className="absolute left-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-navy/70 border border-white/15 text-white hover:bg-gold hover:text-navy transition">
                      <ChevronLeft />
                    </button>
                    <button onClick={next} aria-label="Berikutnya" data-testid="lightbox-next" className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-navy/70 border border-white/15 text-white hover:bg-gold hover:text-navy transition">
                      <ChevronRight />
                    </button>
                  </>
                )}
              </div>

              {photos.length > 1 && (
                <div className="mt-4 flex gap-2 overflow-x-auto pb-2 justify-center">
                  {photos.map((p, k) => (
                    <button
                      key={k}
                      onClick={() => setPhotoIdx(k)}
                      className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition ${k === photoIdx ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`}
                    >
                      <img src={resolveUrl(p)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
